import React, { useState, useEffect, useRef } from 'react';
import contentService from '../services/contentService';

import Loading from './Loading';
const AccountSwitcher = ({ variant = 'topbar' }) => {
  const isSidebar = variant === 'sidebar';
  const [cuentas, setCuentas] = useState([]);
  const [cuentaActual, setCuentaActual] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    cargarCuentas();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cargarCuentas = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await contentService.getCuentas();

      if (response.data.success) {
        setCuentas(response.data.cuentas);
        const activa = response.data.cuentas.find(c => c.activa);
        if (activa) {
          setCuentaActual(activa);
        } else if (response.data.cuentas.length > 0) {
          setCuentaActual(response.data.cuentas[0]);
        } else {
          setError('No tienes cuentas disponibles');
        }
      } else {
        setError('Error al cargar las cuentas');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const cambiarCuenta = async (cuentaId) => {
    try {
      setLoading(true);
      const response = await contentService.cambiarCuenta(cuentaId);
      if (response.data.success) {
        window.location.reload();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cambiar de cuenta');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '10px 16px',
        backgroundColor: '#f3f4f6',
        borderRadius: '10px',
        fontSize: '14px',
        color: '#6b7280'
      }}>
        <Loading inline size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '10px 16px',
        backgroundColor: '#fee2e2',
        borderRadius: '10px',
        fontSize: '14px',
        color: '#dc2626',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>{error}</span>
        <button
          onClick={cargarCuentas}
          style={{
            padding: '4px 8px',
            backgroundColor: 'white',
            border: '1px solid #dc2626',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!cuentaActual) {
    return (
      <div style={{
        padding: '10px 16px',
        backgroundColor: '#fee2e2',
        borderRadius: '10px',
        fontSize: '14px',
        color: '#dc2626'
      }}>
        Sin cuentas
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={() => cuentas.length > 1 && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isSidebar ? '8px' : '12px',
          padding: isSidebar ? '8px 10px' : '10px 16px',
          backgroundColor: isSidebar ? 'transparent' : 'white',
          border: isSidebar ? 'none' : '1px solid #e5e7eb',
          borderRadius: '10px',
          cursor: cuentas.length > 1 ? 'pointer' : 'default',
          minWidth: isSidebar ? 0 : '220px',
          width: isSidebar ? '100%' : undefined,
          fontSize: isSidebar ? '13px' : '14px'
        }}
      >
        {/* Avatar (solo en topbar) */}
        {!isSidebar && (
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0
          }}>
            {cuentaActual.nombre.charAt(0).toUpperCase()}
          </div>
        )}

        {/* Info */}
        <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
          <div style={{
            fontWeight: '600',
            color: isSidebar ? '#fff' : '#111827',
            overflow: isSidebar ? 'hidden' : undefined,
            textOverflow: isSidebar ? 'ellipsis' : undefined,
            whiteSpace: isSidebar ? 'nowrap' : undefined
          }}>
            {cuentaActual.nombre}
          </div>
          {cuentaActual.cliente && cuentaActual.cliente !== cuentaActual.nombre && (
            <div style={{
              fontSize: '12px',
              color: isSidebar ? 'rgba(255,255,255,0.6)' : '#6b7280',
              overflow: isSidebar ? 'hidden' : undefined,
              textOverflow: isSidebar ? 'ellipsis' : undefined,
              whiteSpace: isSidebar ? 'nowrap' : undefined
            }}>
              {cuentaActual.cliente}
            </div>
          )}
        </div>

        {/* Flecha solo si hay más de una cuenta */}
        {cuentas.length > 1 && (
          <span style={{
            fontSize: '12px',
            color: isSidebar ? 'rgba(255,255,255,0.6)' : '#6b7280',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0
          }}>
            ▼
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && cuentas.length > 1 && (
        <div style={{
          position: 'absolute',
          ...(isSidebar
            ? { bottom: 'calc(100% + 8px)' }
            : { top: 'calc(100% + 8px)' }),
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '10px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '12px',
            fontWeight: '600',
            color: '#6b7280',
            textTransform: 'uppercase'
          }}>
            Cambiar cuenta
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {cuentas.map((cuenta) => (
              <button
                key={cuenta.id}
                onClick={() => !cuenta.activa && cambiarCuenta(cuenta.id)}
                disabled={cuenta.activa}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  width: '100%',
                  border: 'none',
                  background: cuenta.activa ? '#f0fdf4' : 'white',
                  cursor: cuenta.activa ? 'default' : 'pointer',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (!cuenta.activa) e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (!cuenta.activa) e.currentTarget.style.background = 'white';
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: cuenta.activa
                    ? 'linear-gradient(135deg, #102826 0%, #69B32D 100%)'
                    : '#e5e7eb',
                  color: cuenta.activa ? 'white' : '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  fontSize: '13px'
                }}>
                  {cuenta.nombre.charAt(0).toUpperCase()}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: cuenta.activa ? '600' : '500',
                    color: '#111827',
                    fontSize: '14px'
                  }}>
                    {cuenta.nombre}
                  </div>
                  {cuenta.cliente && cuenta.cliente !== cuenta.nombre && (
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {cuenta.cliente}
                    </div>
                  )}
                </div>

                {cuenta.activa && (
                  <span style={{
                    color: '#16a34a',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;
